import * as React from 'react';

import { SortableContainer } from 'react-sortable-hoc';

import { Collaborator } from '@services/collaborator/types/collaborator';

import { Option } from '@webapp/types/common/option';

import CollaboratorRowCtrl from './collaborator-row';

interface CollaboratorListCtrlProps {
  className?: string;
  firstBigger?: boolean;
  inHalf?: boolean;
  disabledIds?: string[];
  requiredRole?: string;
  collaborators: Collaborator[];
  roleList: Option[];
  requirePlatformIds?: boolean;
  showErrors?: boolean;
  hasMultiSelect?: boolean;
  currentArtistId?: string;
  removeCollaborator: (collaborator: Collaborator) => void;
  onChange: (data: UpdateCollaboratorsPlatforms[]) => void;
  updateCollaboratorRole: (
    assetId: string,
    collaboratorId: string,
    value: string
  ) => void;
  updateCollaboratorsOrder: (oldIndex: number, newIndex: number) => void;
}

export type UpdateCollaboratorsPlatforms = {
  assetId: string;
  collaboratorId: string;
  platforms?: Record<string, string | undefined>;
};

export const CollaboratorListCtrl =
  SortableContainer<CollaboratorListCtrlProps>(
    ({ collaborators, ...props }: CollaboratorListCtrlProps) => {
      const [collaboratorsPlatforms, setCollaboratorsPlatforms] =
        React.useState<UpdateCollaboratorsPlatforms[]>([]);
      const isDisabled = (value: Partial<Collaborator>) => {
        if (props.disabledIds?.length) {
          if (props.disabledIds.find((i) => i === value.collaboratorId)) {
            return props.requiredRole
              ? value.role === props.requiredRole
              : true;
          }
        }

        if (props.requiredRole) {
          return (
            value.role === props.requiredRole &&
            collaborators.filter((a) => a.role === props.requiredRole).length <=
              1
          );
        }

        return false;
      };

      const handleCollaborator = (
        collaborator: Collaborator,
        platforms: Record<string, string>
      ) => {
        let newCollaborators = collaboratorsPlatforms
          .filter((col) => col.collaboratorId !== collaborator.id)
          .concat({
            assetId: collaborator.assetId,
            collaboratorId: collaborator.id,
            platforms,
          });
        setCollaboratorsPlatforms(newCollaborators);
        props.onChange(newCollaborators);
      };

      return (
        <div className={props.className}>
          {collaborators.map((collaborator: Collaborator, index) => (
            <CollaboratorRowCtrl
              key={`collaborator-list-${collaborator.type}-${collaborator.id}`}
              index={index}
              firstBigger={props.firstBigger}
              inHalf={props.inHalf}
              collaborator={collaborator}
              roleList={props.roleList}
              hasTabSelector={props.roleList.length === 2}
              isDisabled={isDisabled(collaborator)}
              onRemove={() => props.removeCollaborator(collaborator)}
              onUpdatePlatforms={handleCollaborator}
              showErrors={props.showErrors}
              hasMultiSelect={props.hasMultiSelect}
              isActiveArtist={
                props.currentArtistId === collaborator.collaboratorId
              }
              requirePlatformIds={props.requirePlatformIds}
              onChangeRole={(value: string) =>
                props.updateCollaboratorRole(
                  collaborator.assetId,
                  collaborator.id,
                  value
                )
              }
            />
          ))}
        </div>
      );
    }
  );

export default (props: CollaboratorListCtrlProps) => (
  <CollaboratorListCtrl
    {...props}
    onSortEnd={(newSort) =>
      props.updateCollaboratorsOrder(newSort.oldIndex, newSort.newIndex)
    }
    useDragHandle
    lockAxis="y"
    lockToContainerEdges
    helperClass="sorting"
  />
);
